/**
 * Property detail — `GET properties/{id}/`. Full serializer: owner/agent,
 * rates, nested units (each with its own tenant/lease info), images.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PropertyPicture } from '../../components/PropertyPicture';
import { Lightbox } from '../../components/Lightbox';
import { PropertyMap } from '../../components/PropertyMap';
import {
  approveProperty,
  createUnit,
  deleteProperty,
  deletePropertyImage,
  deleteUnit,
  getProperty,
  rejectProperty,
  updateUnit,
  uploadPropertyImage,
} from '../../api/endpoints/properties';
import type { Unit } from '../../types/property';

/**
 * The inline unit form carries exactly the four fields the Flutter app's own
 * unit dialog did (rent_management_app unit editor): everything else on `Unit`
 * keeps its server-side default on create and is left untouched on edit.
 */
const UNIT_INITIAL_FORM = {
  unit_number: '',
  rent_amount: '',
  bedrooms: '1',
  bathrooms: '1',
};

type UnitFormState = typeof UNIT_INITIAL_FORM;

/** `'closed'` | `'create'` | the id of the unit being edited. */
type UnitFormMode = 'closed' | 'create' | number;

function unitToForm(unit: Unit): UnitFormState {
  return {
    unit_number: unit.unit_number,
    rent_amount: unit.rent_amount,
    bedrooms: String(unit.bedrooms),
    bathrooms: unit.bathrooms,
  };
}

export function PropertyDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const propertyId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [unitFormMode, setUnitFormMode] = useState<UnitFormMode>('closed');
  const [unitForm, setUnitForm] = useState<UnitFormState>(UNIT_INITIAL_FORM);
  const [confirmDeleteUnitId, setConfirmDeleteUnitId] = useState<number | null>(null);
  const [confirmDeleteImageId, setConfirmDeleteImageId] = useState<number | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);

  const query = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => getProperty(propertyId),
    enabled: Number.isFinite(propertyId),
  });

  const remove = useMutation({
    mutationFn: () => deleteProperty(propertyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
      navigate('..');
    },
  });

  const approve = useMutation({
    mutationFn: () => approveProperty(propertyId),
    onSuccess: (updated) => queryClient.setQueryData(['property', propertyId], updated),
  });

  const reject = useMutation({
    mutationFn: () => rejectProperty(propertyId),
    onSuccess: (updated) => queryClient.setQueryData(['property', propertyId], updated),
  });

  // Units are nested inside the property detail response, so every unit/image
  // mutation invalidates ['property', propertyId] rather than a list key.
  function invalidateProperty(): void {
    void queryClient.invalidateQueries({ queryKey: ['property', propertyId] });
  }

  const saveUnit = useMutation({
    mutationFn: () => {
      const payload = {
        unit_number: unitForm.unit_number,
        rent_amount: unitForm.rent_amount,
        bedrooms: unitForm.bedrooms === '' ? undefined : Number(unitForm.bedrooms),
        bathrooms: unitForm.bathrooms === '' ? undefined : unitForm.bathrooms,
      };
      return typeof unitFormMode === 'number'
        ? updateUnit(unitFormMode, payload)
        : createUnit({ ...payload, property: propertyId });
    },
    onSuccess: () => {
      invalidateProperty();
      setUnitFormMode('closed');
      setUnitForm(UNIT_INITIAL_FORM);
    },
  });

  const removeUnit = useMutation({
    mutationFn: (unitId: number) => deleteUnit(unitId),
    onSuccess: invalidateProperty,
  });

  const removeImage = useMutation({
    mutationFn: (imageId: number) => deletePropertyImage(imageId),
    onSuccess: invalidateProperty,
  });

  const uploadImage = useMutation({
    mutationFn: (file: File) => uploadPropertyImage(propertyId, file),
    onSuccess: () => {
      invalidateProperty();
      setSelectedImageFile(null);
      // Remounts the file input — the only reliable way to clear a `type="file"`
      // value, since it can't be set programmatically for security reasons.
      setImageInputKey((key) => key + 1);
    },
  });

  function updateUnitForm(field: keyof UnitFormState, value: string): void {
    setUnitForm((previous) => ({ ...previous, [field]: value }));
  }

  function handleUnitSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    saveUnit.mutate();
  }

  function handleImageUploadSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (selectedImageFile !== null) uploadImage.mutate(selectedImageFile);
  }

  if (!Number.isFinite(propertyId)) {
    return <p>Invalid property id.</p>;
  }

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const property = query.data;
  const viewableImages = property.images
    .filter((image): image is typeof image & { image: string } => image.image !== null)
    .map((image) => ({
      src: image.image,
      alt: image.caption === '' ? `Property image ${image.id}` : image.caption,
    }));
  const latitude = property.latitude !== null ? Number(property.latitude) : null;
  const longitude = property.longitude !== null ? Number(property.longitude) : null;
  const hasCoordinates =
    latitude !== null && longitude !== null && Number.isFinite(latitude) && Number.isFinite(longitude);

  return (
    <section>
      <h2>{property.name}</h2>
      <p>
        <Link to="edit">Edit</Link>{' '}
        <button type="button" className="button--danger" onClick={() => setConfirmDeleteOpen(true)}>
          Delete
        </button>
      </p>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete property"
        message={`Delete "${property.name}"? This cannot be undone.`}
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          setConfirmDeleteOpen(false);
          remove.mutate();
        }}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      {(approve.isError || reject.isError || remove.isError || removeUnit.isError ||
        removeImage.isError) && (
        <p role="alert" className="form__error">
          {
            parseApiError(
              approve.error ?? reject.error ?? remove.error ?? removeUnit.error ??
                removeImage.error,
            ).message
          }
        </p>
      )}

      {user?.role === 'SUPERAGENT' && property.status === 'PENDING_APPROVAL' && (
        <p>
          <button type="button" disabled={approve.isPending} onClick={() => approve.mutate()}>
            Approve
          </button>{' '}
          <button type="button" disabled={reject.isPending} onClick={() => reject.mutate()}>
            Reject
          </button>
        </p>
      )}

      <dl>
        <dt>Status</dt>
        <dd>{property.status}</dd>
        <dt>Type</dt>
        <dd>{property.property_type}</dd>
        <dt>Address</dt>
        <dd>
          {property.address}, {property.city}, {property.state} County
          {property.zip_code !== '' && ` ${property.zip_code}`}, {property.country}
        </dd>
        <dt>Owner</dt>
        <dd>{property.owner_name}</dd>
        <dt>Agent</dt>
        <dd>{property.agent_name ?? 'Unassigned'}</dd>
        <dt>Water rate</dt>
        <dd>{property.water_rate}</dd>
        <dt>Electricity rate</dt>
        <dd>{property.electricity_rate}</dd>
        <dt>WhatsApp</dt>
        <dd>{property.whatsapp_number === '' ? '—' : property.whatsapp_number}</dd>
        <dt>Description</dt>
        <dd>{property.description === '' ? '—' : property.description}</dd>
      </dl>

      <h3>Location</h3>
      {hasCoordinates && latitude !== null && longitude !== null ? (
        <PropertyMap latitude={latitude} longitude={longitude} label={property.name} />
      ) : (
        <p>Location coordinates not available.</p>
      )}

      <h3>Units ({property.units.length})</h3>

      <p>
        <button
          type="button"
          onClick={() => {
            setUnitForm(UNIT_INITIAL_FORM);
            setUnitFormMode('create');
          }}
        >
          Add unit
        </button>
      </p>

      {unitFormMode !== 'closed' && (
        <form onSubmit={handleUnitSubmit} noValidate>
          <h4>{unitFormMode === 'create' ? 'Add unit' : 'Edit unit'}</h4>

          {saveUnit.isError && (
            <p role="alert" className="form__error">
              {parseApiError(saveUnit.error).message}
            </p>
          )}

          <label htmlFor="unit-number">Unit number</label>
          <input
            id="unit-number"
            required
            value={unitForm.unit_number}
            onChange={(event) => updateUnitForm('unit_number', event.target.value)}
          />

          <label htmlFor="unit-rent">Rent amount</label>
          <input
            id="unit-rent"
            required
            value={unitForm.rent_amount}
            onChange={(event) => updateUnitForm('rent_amount', event.target.value)}
          />

          <label htmlFor="unit-bedrooms">Bedrooms</label>
          <input
            id="unit-bedrooms"
            type="number"
            min="0"
            value={unitForm.bedrooms}
            onChange={(event) => updateUnitForm('bedrooms', event.target.value)}
          />

          <label htmlFor="unit-bathrooms">Bathrooms</label>
          <input
            id="unit-bathrooms"
            value={unitForm.bathrooms}
            onChange={(event) => updateUnitForm('bathrooms', event.target.value)}
          />

          <button type="submit" disabled={saveUnit.isPending}>
            {saveUnit.isPending ? 'Saving…' : 'Save unit'}
          </button>{' '}
          <button type="button" onClick={() => setUnitFormMode('closed')}>
            Cancel
          </button>
        </form>
      )}

      {property.units.length === 0 && <p>No units.</p>}
      {property.units.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Unit</th>
              <th>Beds/Baths</th>
              <th>Rent</th>
              <th>Vacant</th>
              <th>Tenant</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {property.units.map((unit) => (
              <tr key={unit.id}>
                <td>{unit.unit_number}</td>
                <td>
                  {unit.bedrooms} bd / {unit.bathrooms} ba
                </td>
                <td>{unit.rent_amount}</td>
                <td>{unit.is_vacant ? 'Yes' : 'No'}</td>
                <td>{unit.tenant_name ?? '—'}</td>
                <td>
                  <span className="row-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setUnitForm(unitToForm(unit));
                        setUnitFormMode(unit.id);
                      }}
                    >
                      <Pencil size={14} className="icon-inline" aria-hidden="true" />
                      Edit
                    </button>
                    <button
                      type="button"
                      className="button--danger"
                      disabled={removeUnit.isPending}
                      onClick={() => setConfirmDeleteUnitId(unit.id)}
                    >
                      <Trash2 size={14} className="icon-inline" aria-hidden="true" />
                      Delete
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        open={confirmDeleteUnitId !== null}
        title="Delete unit"
        message="Delete this unit? This cannot be undone."
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDeleteUnitId !== null) removeUnit.mutate(confirmDeleteUnitId);
          setConfirmDeleteUnitId(null);
        }}
        onCancel={() => setConfirmDeleteUnitId(null)}
      />

      <h3>Images ({property.images.length})</h3>
      {property.images.length === 0 && <p>No images uploaded.</p>}
      {property.images.length > 0 && (
        <ul className="gallery">
          {property.images.map((image) => (
            <li key={image.id} className="gallery__item">
              {image.is_primary && <span className="gallery__badge">Primary</span>}
              {image.image !== null ? (
                <button
                  type="button"
                  className="gallery__trigger"
                  onClick={() =>
                    setLightboxIndex(viewableImages.findIndex((viewable) => viewable.src === image.image))
                  }
                >
                  <PropertyPicture
                    src={image.image}
                    alt={image.caption === '' ? `Property image ${image.id}` : image.caption}
                    size="medium"
                  />
                </button>
              ) : (
                <span className="thumb thumb--placeholder" aria-hidden="true">
                  —
                </span>
              )}
              {image.caption !== '' && <span className="gallery__caption">{image.caption}</span>}
              {/* Separate click target from `.gallery__trigger` above so opening
                  the lightbox and deleting never overlap. */}
              <button
                type="button"
                className="gallery__delete button--danger"
                aria-label={`Delete image ${image.id}`}
                disabled={removeImage.isPending}
                onClick={() => setConfirmDeleteImageId(image.id)}
              >
                <Trash2 size={14} className="icon-inline" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={confirmDeleteImageId !== null}
        title="Delete image"
        message="Delete this image? This cannot be undone."
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDeleteImageId !== null) removeImage.mutate(confirmDeleteImageId);
          setConfirmDeleteImageId(null);
        }}
        onCancel={() => setConfirmDeleteImageId(null)}
      />
      <form onSubmit={handleImageUploadSubmit} noValidate>
        {uploadImage.isError && (
          <p role="alert" className="form__error">
            {parseApiError(uploadImage.error).message}
          </p>
        )}
        <label htmlFor="property-image-upload">Upload image</label>{' '}
        <input
          key={imageInputKey}
          id="property-image-upload"
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={(event) => setSelectedImageFile(event.target.files?.[0] ?? null)}
        />{' '}
        <button type="submit" disabled={selectedImageFile === null || uploadImage.isPending}>
          {uploadImage.isPending ? 'Uploading…' : 'Upload'}
        </button>
      </form>

      <Lightbox
        images={viewableImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
    </section>
  );
}

export default PropertyDetailPage;
